import { Col, FormControl, FormGroup, FormLabel } from "react-bootstrap";
import { Control, Controller, FormState, useWatch } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { ErrorFieldMessage } from "../../extras/components/ErrorFieldMessage";
import {
  BUNDLE_RESTRICTION_TYPE_MESSAGE,
  BUNDLE_RESTRICTION_TYPE_WITHOUT_CONSTANT_MESSAGE,
  messages,
} from "../EditBundle/messages";
import { WitLabel } from "../EditBundle/styles";
import { BundleRestrictions, BundleRestrictionType } from "../EditBundle/types";
import { SelectGroup } from "./SelectGroup";

export type BundleRestrictionsFormType = {
  restrictions: BundleRestrictions;
};

export interface BundleRestrictionsFormProps {
  control: Control<BundleRestrictionsFormType>;
  formState: FormState<BundleRestrictionsFormType>;
}

export function BundleRestrictionsForm({ control, formState }: BundleRestrictionsFormProps) {
  const watchMinStayType = useWatch({ control, name: "restrictions.minStayType" });
  const watchMaxStayType = useWatch({ control, name: "restrictions.maxStayType" });
  const watchMinNoticeType = useWatch({ control, name: "restrictions.minNoticeType" });
  const watchMaxNoticeType = useWatch({ control, name: "restrictions.maxNoticeType" });

  return (
    <FormGroup as={Col}>
      <FormLabel column sm={12}>
        <WitLabel>
          <FormattedMessage {...messages.restrictions} />
        </WitLabel>
      </FormLabel>

      <FormGroup>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.minStay} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="restrictions.minStayType"
            control={control}
            render={({ field }) => (
              <SelectGroup optionDisplay={BUNDLE_RESTRICTION_TYPE_MESSAGE} {...field} />
            )}
          />
        </Col>
      </FormGroup>
      {watchMinStayType === BundleRestrictionType.CONSTANT && (
        <FormGroup>
          <Col sm={3}>
            <Controller
              rules={{ min: 2 }}
              name="restrictions.minStay"
              control={control}
              render={({ field }) => <FormControl {...field} />}
            />
            {formState.errors.restrictions?.minStay?.type === "min" && (
              <ErrorFieldMessage {...messages.minValueValidator} values={{ minValue: 2 }} />
            )}
          </Col>
        </FormGroup>
      )}

      <FormGroup>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.maxStay} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="restrictions.maxStayType"
            control={control}
            render={({ field }) => (
              <SelectGroup optionDisplay={BUNDLE_RESTRICTION_TYPE_MESSAGE} {...field} />
            )}
          />
        </Col>
      </FormGroup>
      {watchMaxStayType === BundleRestrictionType.CONSTANT && (
        <FormGroup>
          <Col sm={3}>
            <Controller
              rules={{ min: 1 }}
              name="restrictions.maxStay"
              control={control}
              render={({ field }) => <FormControl {...field} />}
            />
            {formState.errors.restrictions?.maxStay?.type === "min" && (
              <ErrorFieldMessage {...messages.minValueValidator} values={{ minValue: 1 }} />
            )}
          </Col>
        </FormGroup>
      )}

      <FormGroup>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.minNotice} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="restrictions.minNoticeType"
            control={control}
            render={({ field }) => (
              <SelectGroup optionDisplay={BUNDLE_RESTRICTION_TYPE_MESSAGE} {...field} />
            )}
          />
        </Col>
      </FormGroup>
      {watchMinNoticeType === BundleRestrictionType.CONSTANT && (
        <FormGroup>
          <Col sm={3}>
            <Controller
              rules={{ min: 1 }}
              name="restrictions.minNotice"
              control={control}
              render={({ field }) => <FormControl {...field} />}
            />
            {formState.errors.restrictions?.minNotice?.type === "min" && (
              <ErrorFieldMessage {...messages.minValueValidator} values={{ minValue: 1 }} />
            )}
          </Col>
        </FormGroup>
      )}

      <FormGroup>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.maxNotice} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="restrictions.maxNoticeType"
            control={control}
            render={({ field }) => (
              <SelectGroup optionDisplay={BUNDLE_RESTRICTION_TYPE_MESSAGE} {...field} />
            )}
          />
        </Col>
      </FormGroup>
      {watchMaxNoticeType === BundleRestrictionType.CONSTANT && (
        <FormGroup>
          <Col sm={3}>
            <Controller
              rules={{ min: 1 }}
              name="restrictions.maxNotice"
              control={control}
              render={({ field }) => <FormControl {...field} />}
            />
            {formState.errors.restrictions?.maxNotice?.type === "min" && (
              <ErrorFieldMessage {...messages.minValueValidator} values={{ minValue: 1 }} />
            )}
          </Col>
        </FormGroup>
      )}

      <FormGroup>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.closeToArrival} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="restrictions.closeToArrivalType"
            control={control}
            render={({ field }) => (
              <SelectGroup
                optionDisplay={BUNDLE_RESTRICTION_TYPE_WITHOUT_CONSTANT_MESSAGE}
                {...field}
              />
            )}
          />
        </Col>
      </FormGroup>

      <FormGroup>
        <FormLabel column sm={12}>
          <FormattedMessage {...messages.closeToDeparture} />
        </FormLabel>
        <Col sm={3}>
          <Controller
            name="restrictions.closeToDepartureType"
            control={control}
            render={({ field }) => (
              <SelectGroup
                optionDisplay={BUNDLE_RESTRICTION_TYPE_WITHOUT_CONSTANT_MESSAGE}
                {...field}
              />
            )}
          />
        </Col>
      </FormGroup>
    </FormGroup>
  );
}
